// packages
import cx from 'classnames';
import { geoIdentity, GeoIdentityTransform } from 'd3-geo';
import { Component, h } from 'preact';

// components
import asyncComponent from './components/AsyncComponent';
import CandidateRow from './components/CandidateRow';
import Contest from './components/Contest';
import HeadshotCandidateRow from './components/HeadshotCandidateRow';
import PymChild from './components/PymChild';

// utils

import getJSON from './utils/get-json';
import { determineDistrictTitle } from './election-utils';
import { FLAT_RESULTS_URL } from './endpoints';
import { LENGTH_OF_CHECK_INTERVAL } from './constants';

import topology from 'Assets/geography/texas.json';

const LayeredMap = asyncComponent(() =>
  import(/* webpackChunkName: "LayeredMap" */ './components/LayeredMap')
);

interface Props {
  contestIds: string[];
}

interface State {
  contests: any[];
}

class InternalEmbedApp extends Component<Props, State> {
  private projection: GeoIdentityTransform;

  constructor() {
    super();

    this.state = { contests: [] };
    this.projection = geoIdentity().reflectY(true);
    this.fetchData = this.fetchData.bind(this);
  }

  fetchData() {
    const { contestIds } = this.props;

    getJSON(FLAT_RESULTS_URL).then((contests: any[]) => {
      const activeContests = contests.filter(contest => {
        const slug = `${contest.office_slug}${
          contest.division_type === 'statewide' ? '' : `-${contest.district}`
        }`;

        return contestIds.indexOf(slug) >= 0;
      });

      this.setState({ contests: activeContests }, this.startTimer);
    });
  }

  startTimer() {
    this.timer = window.setTimeout(this.fetchData, LENGTH_OF_CHECK_INTERVAL);
  }

  clearTimer() {
    window.clearTimeout(this.timer);
  }

  componentDidMount() {
    this.fetchData();
  }

  componentWillUnmount() {
    this.clearTimer();
  }

  render() {
    const { contests } = this.state;

    const numberOfContests = contests.length;
    const shouldGetBreakout =
      numberOfContests === 1 && contests[0].division_type === 'statewide';

    const Row = shouldGetBreakout ? HeadshotCandidateRow : CandidateRow;

    return (
      <PymChild
        class={cx('embed', { 'embed--breakout': shouldGetBreakout })}
        polling={1000}
      >
        {contests.map(contest => (
          <Contest
            key={contest.id}
            contest={contest}
            columnCount={Row.columnCount}
            contestTitle={determineDistrictTitle(contest)}
            tableHeader={Row.Header}
            renderCandidate={(
              candidate: any,
              { totalVotes }: { totalVotes: number }
            ) => (
              <Row
                key={candidate.id}
                ballotName={candidate.ballot_name}
                candidateId={candidate.id}
                contestIsCalled={contest.is_called}
                isIncumbent={candidate.is_incumbent}
                isWinner={candidate.is_winner}
                party={candidate.party}
                totalVotesInContest={totalVotes}
                votes={candidate.votes}
              />
            )}
          />
        ))}
        {shouldGetBreakout && (
          <LayeredMap
            topology={topology}
            projection={this.projection}
            candidates={contests[0].candidates}
            counties={contests[0].counties}
          />
        )}
      </PymChild>
    );
  }
}

export default InternalEmbedApp;
